import { FormEvent, useMemo, useState } from 'react';
import { useMedicinesCatalog } from '../context/MedicinesCatalogContext';
import type { AdministrationRoute, Medicine } from '../types/medicine';
import { ROUTE_OPTIONS } from '../types/medicine';

const ITEMS_PER_PAGE = 20;

export default function MedicinesPage() {
  const { medicines, setMedicines } = useMedicinesCatalog();
  const [name, setName] = useState('');
  const [route, setRoute] = useState<AdministrationRoute>('Oral');
  const [quantity, setQuantity] = useState<number>(1);
  const [unitPrice, setUnitPrice] = useState<number>(1);
  const [nameFilter, setNameFilter] = useState('');
  const [routeFilters, setRouteFilters] = useState<AdministrationRoute[]>([]);
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMedicineId, setEditingMedicineId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Medicine | null>(null);

  const filteredMedicines = useMemo(() => {
    return medicines.filter((medicine) => {
      const matchesName = medicine.name.toLowerCase().includes(nameFilter.toLowerCase().trim());
      const matchesRoute = routeFilters.length === 0 || routeFilters.includes(medicine.route);
      return matchesName && matchesRoute;
    });
  }, [medicines, nameFilter, routeFilters]);

  const totalPages = Math.max(1, Math.ceil(filteredMedicines.length / ITEMS_PER_PAGE));
  const safePage = Math.min(page, totalPages);

  const paginatedMedicines = useMemo(() => {
    const start = (safePage - 1) * ITEMS_PER_PAGE;
    return filteredMedicines.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredMedicines, safePage]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!name.trim() || quantity <= 0 || unitPrice <= 0) {
      return;
    }

    if (editingMedicineId !== null) {
      setMedicines((prev) =>
        prev.map((medicine) =>
          medicine.id === editingMedicineId
            ? { ...medicine, name: name.trim(), route, quantity, unitPrice }
            : medicine
        )
      );
    } else {
      const newMedicine: Medicine = {
        id: medicines.length > 0 ? Math.max(...medicines.map((item) => item.id)) + 1 : 1,
        categoryKey: 'farmacia',
        name: name.trim(),
        brand: '',
        manufacturer: '',
        description: '',
        concentration: null,
        pharmaceuticalForm: '',
        presentation: '',
        sanitaryRegistrationNumber: '',
        activeIngredient: '',
        route,
        quantity,
        unitPrice,
        tags: [],
      };
      setMedicines((prev) => [newMedicine, ...prev]);
    }

    setName('');
    setRoute('Oral');
    setQuantity(1);
    setUnitPrice(1);
    setEditingMedicineId(null);
    setIsModalOpen(false);
    setPage(1);
  };

  const toggleRouteFilter = (value: AdministrationRoute) => {
    setRouteFilters((prev) => {
      if (prev.includes(value)) {
        return prev.filter((routeItem) => routeItem !== value);
      }
      return [...prev, value];
    });
    setPage(1);
  };

  const openAddModal = () => {
    setEditingMedicineId(null);
    setName('');
    setRoute('Oral');
    setQuantity(1);
    setUnitPrice(1);
    setIsModalOpen(true);
  };

  const openEditModal = (medicine: Medicine) => {
    setEditingMedicineId(medicine.id);
    setName(medicine.name);
    setRoute(medicine.route);
    setQuantity(medicine.quantity);
    setUnitPrice(medicine.unitPrice);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingMedicineId(null);
  };

  const openDeleteModal = (medicine: Medicine) => {
    setDeleteTarget(medicine);
  };

  const closeDeleteModal = () => {
    setDeleteTarget(null);
  };

  const confirmDelete = () => {
    if (!deleteTarget) {
      return;
    }

    setMedicines((prev) => prev.filter((medicine) => medicine.id !== deleteTarget.id));
    setDeleteTarget(null);
    setPage(1);
  };

  return (
    <section className="card content-card">
      <header className="module-header">
        <h2>Modulo de Medicinas</h2>
        <p className="subtitle">Formulario de alta, filtros y listado paginado de medicamentos.</p>
        <button type="button" className="add-medicine-button" onClick={openAddModal}>
          Agregar medicina
        </button>
      </header>

      <div className="medicine-filters card-section">
        <h3 className="section-title">Filtros</h3>
        <div className="field-group">
          <label htmlFor="name-filter">Filtro por Nombre</label>
          <input
            id="name-filter"
            type="text"
            placeholder="Buscar por nombre..."
            value={nameFilter}
            onChange={(event) => {
              setNameFilter(event.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="route-filter-group field-group">
          <span>Filtro por Via</span>
          <div className="route-filter-options">
            {ROUTE_OPTIONS.map((option) => (
              <label key={option} className="route-filter-option">
                <input
                  type="checkbox"
                  checked={routeFilters.includes(option)}
                  onChange={() => toggleRouteFilter(option)}
                />
                {option}
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="table-wrapper card-section">
        <div className="table-header">
          <h3 className="section-title">Listado de productos</h3>
          <span className="subtitle">
            Mostrando {paginatedMedicines.length} de {filteredMedicines.length}
          </span>
        </div>
        <table className="medicine-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Via</th>
              <th>Cantidad</th>
              <th>Precio Unitario</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {paginatedMedicines.map((medicine) => (
              <tr key={medicine.id}>
                <td>{medicine.name}</td>
                <td>{medicine.route}</td>
                <td>{medicine.quantity}</td>
                <td>S/. {medicine.unitPrice.toFixed(2)}</td>
                <td className="table-actions-cell">
                  <button type="button" className="table-action-button" onClick={() => openEditModal(medicine)}>
                    Editar
                  </button>
                  <button
                    type="button"
                    className="table-action-button table-action-button-danger"
                    onClick={() => openDeleteModal(medicine)}
                  >
                    Eliminar
                  </button>
                </td>
              </tr>
            ))}
            {paginatedMedicines.length === 0 ? (
              <tr>
                <td colSpan={5} className="empty-row">
                  No hay resultados con los filtros actuales.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button type="button" onClick={() => setPage((prev) => Math.max(prev - 1, 1))} disabled={safePage === 1}>
          Anterior
        </button>
        <span>
          Pagina {safePage} de {totalPages} ({filteredMedicines.length} resultados)
        </span>
        <button
          type="button"
          onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={safePage === totalPages}
        >
          Siguiente
        </button>
      </div>

      {isModalOpen ? (
        <div className="modal-overlay" onClick={closeModal} role="presentation">
          <div className="modal-content" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true">
            <div className="modal-header">
              <h3>{editingMedicineId ? 'Editar medicamento' : 'Agregar medicamento'}</h3>
              <button type="button" className="modal-close" onClick={closeModal}>
                x
              </button>
            </div>

            <form className="medicine-form" onSubmit={handleSubmit}>
              <div className="medicine-form-grid">
                <div className="field-group field-row-full">
                  <label htmlFor="medicine-name">Nombre del medicamento</label>
                  <input
                    id="medicine-name"
                    type="text"
                    placeholder="Ej. Paracetamol 500mg"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                  />
                </div>

                <div className="field-group field-row-half">
                  <label htmlFor="medicine-route">Via de administracion</label>
                  <select
                    id="medicine-route"
                    value={route}
                    onChange={(event) => setRoute(event.target.value as AdministrationRoute)}
                  >
                    {ROUTE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field-group field-row-half">
                  <label htmlFor="medicine-quantity">Cantidad</label>
                  <input
                    id="medicine-quantity"
                    type="number"
                    min={1}
                    value={quantity}
                    onChange={(event) => setQuantity(Number(event.target.value))}
                  />
                </div>

                <div className="field-group field-row-full">
                  <label htmlFor="medicine-price">Precio Unitario</label>
                  <div className="price-input-wrapper">
                    <span className="price-prefix">S/.</span>
                    <input
                      id="medicine-price"
                      type="number"
                      min={0.01}
                      step={0.01}
                      value={unitPrice}
                      onChange={(event) => setUnitPrice(Number(event.target.value))}
                    />
                  </div>
                </div>
              </div>
              <div className="medicine-form-actions">
                <button type="button" className="secondary-button" onClick={closeModal}>
                  Cancelar
                </button>
                <button type="submit">{editingMedicineId ? 'Guardar cambios' : 'Agregar medicamento'}</button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="modal-overlay" onClick={closeDeleteModal} role="presentation">
          <div className="modal-content modal-content-sm" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true">
            <div className="modal-header">
              <h3>Confirmar eliminacion</h3>
              <button type="button" className="modal-close" onClick={closeDeleteModal}>
                x
              </button>
            </div>
            <p className="subtitle">
              Estas seguro de eliminar <strong>{deleteTarget.name}</strong>? Esta accion no se puede deshacer.
            </p>
            <div className="medicine-form-actions">
              <button type="button" className="secondary-button" onClick={closeDeleteModal}>
                Cancelar
              </button>
              <button type="button" className="danger-button" onClick={confirmDelete}>
                Eliminar
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
